<template>
  <div class="page-billing">
    <resize-observer @notify="__resizeHandler" />
    <div v-if="!isResellerAccount && partnerType === 'RESELLER'">
      <billing-plot
        ref="billingPlot"
        name="Billing Activity"
        :plot-data="billingRecords"
        :currentBalance="currentBalance"
        :loading="dataLoading"
      />
      <billing-records :records="billingRecords" :loading="dataLoading" />
    </div>

    <div v-if="isResellerAccount && partnerType === 'RESELLER'">
      <reseller-billing-plot
        ref="billingPlot"
        name="Billing Activity"
        :plot-data="billingRecords"
        :currentBalance="currentBalance"
        :loading="dataLoading"
      />
      <div class="mt-10">
        <el-tabs v-model="activeTab">
          <el-tab-pane name="Account Details" label="Account Details">
            <reseller-billing-details
              :records="billingRecords"
              :loading="dataLoading"
            />
          </el-tab-pane>

          <el-tab-pane name="Invoices" label="Invoices">
            <reseller-billing-records
              :records="billingRecords"
              :loading="dataLoading"
            />
          </el-tab-pane>

          <el-tab-pane name="Partner Inventory" label="Partner Inventory">
            <partner-inventory :months="months" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <div v-if="isResellerAccount && partnerType === 'WHITELABEL'">
      <white-label-billing-plot
        ref="billingPlot"
        name="Billing Activity"
        :plot-data="plotData"
        :currentBalance="currentBalance"
        :loading="dataLoading"
      />
      <div class="mt-10">
        <el-tabs v-model="activeTab">
          <el-tab-pane name="Account Details" label="Account Details">
            <white-label-billing-details
              :records="billingRecords"
              :loading="dataLoading"
            />
          </el-tab-pane>

          <el-tab-pane name="Invoices" label="Invoices">
            <white-label-billing-records
              :records="billingRecords"
              :loading="dataLoading"
            />
          </el-tab-pane>

          <el-tab-pane name="Partner Inventory" label="Partner Inventory">
            <partner-inventory :months="months" />
          </el-tab-pane>
        </el-tabs>
      </div>
    </div>

    <div v-if="!isResellerAccount && partnerType === 'WHITELABEL'">
      <h2 class="mb-0 mt-0">
        No available billing data
      </h2>
      <p>
        The selected account is under a white label umbrella.
      </p>
    </div>
  </div>
</template>

<script>
import BillingPlot from "./components/billing-plot";
import BillingRecords from "./components/billing-records-table";
import PartnerInventory from "./components/partner-inventory";
import ResellerBillingPlot from "./components/reseller/billing-plot";
import ResellerBillingDetails from "./components/reseller/billing-details-table";
import ResellerBillingRecords from "./components/reseller/billing-records-table";
import WhiteLabelBillingPlot from "./components/whitelabel/billing-plot";
import WhiteLabelBillingDetails from "./components/whitelabel/billing-details-table";
import WhiteLabelBillingRecords from "./components/whitelabel/billing-records-table";
import feathersClient from "@/feathers/feathers-client";
import moment from "moment-timezone";

export default {
  name: "BillingsTable",

  components: {
    BillingPlot,
    BillingRecords,
    ResellerBillingPlot,
    ResellerBillingRecords,
    ResellerBillingDetails,
    PartnerInventory,
    WhiteLabelBillingRecords,
    WhiteLabelBillingDetails,
    WhiteLabelBillingPlot
  },
  data() {
    return {
      resized: false,
      billingRecords: [],
      currentBalance: null,
      isQbCustomer: false,
      dataLoading: false,
      months: [],
      plotData: [],
      activeTab: "Account Details"
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
    isResellerAccount() {
      return this.selectedTenant
        ? this.selectedTenant.isResellerAccount
        : false;
    },
    partnerType() {
      return this.selectedTenant ? this.selectedTenant.resellerType : false;
    }
  },

  watch: {
    user() {
      if (this.user) {
        this.findBillings();
      }
    },
    selectedTenant() {
      this.findBillings();
    }
  },

  methods: {
    __resizeHandler: _.throttle(function(e) {
      if (this.resized) {
        setTimeout(() => {
          this.$refs.billingPlot.chart.resize();
        }, 1000);
      }
      this.resized = true;
    }, 700),

    setMonths() {
      this.months = this.billingRecords.map(item => ({
        label: moment(item.date).format("MMMM - YYYY"),
        value: item.date
      }));
    },

    findBillings() {
      this.billingRecords = [];
      this.dataLoading = true;
      feathersClient
        .service("billing")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            $sort: {
              id: -1
            }
          }
        })
        .then(res => {
          this.billingRecords = [...res.billings];
          this.setMonths();

          if (this.isResellerAccount && this.partnerType === "WHITELABEL") {
            this.plotData = this.buildPlotData();
          }

          this.currentBalance = res.currentBalance;
          this.isQbCustomer = res.isQbCustomer;
          this.dataLoading = false;
        })
        .catch(e => {
          this.dataLoading = false;
        });
    },

    buildPlotData() {
      const partnerMetrics = {
        "Partner Residential Seat": "residentialSeats",
        "Partner Unlimited Extension Seat": "extensions",
        "Partner Fax Service": "faxes",
        "Partner Toll Free Service": "tollFree",
        "Partner International Minutes": "internationalMinutes",
        "Partner SIP Trunk": "sipTrunks"
      };

      const data = [];
      for (let i = 0; i < this.billingRecords.length; i++) {
        const record = this.billingRecords[i];
        const { date, amount } = record;
        const { lineItems } = record.data;

        const plotObject = {
          date,
          total: amount
        };

        for (let item of lineItems) {
          const { product, qty } = item;

          if (partnerMetrics[product]) {
            plotObject[partnerMetrics[product]] = qty;
          }
        }

        data.push(plotObject);
      }

      return data;
    }
  },
  mounted() {
    if (this.user) {
      this.findBillings();
    }
  }
};
</script>
<style scoped>
.page-billing {
  overflow: scroll;
}
</style>
